'use client'

import { Wrench, ExternalLink, Copy, Eye, Users, Building2, Database, Activity, Video } from 'lucide-react'
import { useSession } from 'next-auth/react'
import { useEffect, useState } from 'react'

import { isSuperAdmin } from '@server/auth/is-superadmin'

import { Badge } from '@client/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@client/components/ui/dialog'
import { useToast } from '@client/hooks/use-toast'
import { getTemporalWorkflowUrl, getSupabaseStudioUrl, getPostHogReplayUrl } from '@client/lib/dev-toolkit-config'
import { markPostImpersonationExit } from '@client/lib/impersonation-exit-flag'
import { R3AdminClient } from '@client/lib/r3-admin-client'

import { usePageContext } from './usePageContext'
import { useSupabaseTableIds } from './useSupabaseTableIds'

type ItemIcon = typeof ExternalLink

function ToolkitRow({
  icon: Icon,
  label,
  href,
  onClick,
  disabled,
  disabledReason,
  external = true,
}: {
  icon: ItemIcon
  label: string
  href?: string | null
  onClick?: () => void
  disabled?: boolean
  disabledReason?: string
  external?: boolean
}) {
  const isLink = href !== undefined
  const isDisabled = disabled || (isLink && !href)

  const inner = (
    <>
      <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
      <span className="flex-1 truncate text-left">{label}</span>
      {isLink && external && <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />}
    </>
  )

  const classes =
    'flex w-full items-center gap-2 rounded px-3 py-2 text-sm hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent'

  if (isDisabled) {
    return (
      <button className={classes} disabled title={disabledReason}>
        {inner}
      </button>
    )
  }

  if (href) {
    const linkProps = external ? { target: '_blank' as const, rel: 'noopener noreferrer' } : {}
    return (
      <a href={href} {...linkProps} className={classes} onClick={onClick}>
        {inner}
      </a>
    )
  }

  return (
    <button className={classes} onClick={onClick}>
      {inner}
    </button>
  )
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <p className="px-3 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{children}</p>
  )
}

function shouldIgnoreKeyEvent(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  const tag = target.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
  if (target.isContentEditable) return true
  return false
}

export function DevToolkit() {
  const { data: session, update } = useSession()
  const { toast } = useToast()
  const { sessionId, orgCode, userId: pageUserId } = usePageContext()
  const tableIds = useSupabaseTableIds()
  const [open, setOpen] = useState(false)
  const [exiting, setExiting] = useState(false)
  const [orgMemberIds, setOrgMemberIds] = useState<string[] | null>(null)

  const visible = !!session && (isSuperAdmin(session) || session.impersonating)

  // Lazy-fetch member user ids for the "sessions by org members" PostHog link
  // when the popover is opened on an org context.
  useEffect(() => {
    if (!open || !orgCode) return
    let cancelled = false
    setOrgMemberIds(null)
    R3AdminClient.getAdminOrganizationDetail(orgCode)
      .then((data) => {
        if (!cancelled) setOrgMemberIds(data.members.map((m) => m.userId))
      })
      .catch(() => {
        if (!cancelled) setOrgMemberIds([])
      })
    return () => {
      cancelled = true
    }
  }, [open, orgCode])

  // Listen for `~` to open/close. Only attached when visible so we don't
  // intercept the keystroke for non-admin users.
  useEffect(() => {
    if (!visible) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== '~') return
      if (shouldIgnoreKeyEvent(e.target)) return
      e.preventDefault()
      setOpen((v) => !v)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [visible])

  if (!visible) return null

  const signedInEmail = session.user.email
  const signedInId = session.user.id
  const signedInPosthog = getPostHogReplayUrl({ type: 'users', userIds: [signedInId] })
  const pagePosthog = pageUserId ? getPostHogReplayUrl({ type: 'users', userIds: [pageUserId] }) : null
  const hasLeftContext = !!session.impersonating || !!sessionId || !!orgCode || !!pageUserId

  const copy = (value: string, label: string) => {
    void navigator.clipboard.writeText(value).then(
      () => toast({ title: `${label} copied`, description: value }),
      () => toast({ title: `Failed to copy ${label}`, variant: 'destructive' })
    )
  }

  const exitImpersonation = async () => {
    setExiting(true)
    const returnPath = session.impersonationReturnPath
    await update({ stopImpersonation: true })
    const dest = returnPath && returnPath.startsWith('/') && !returnPath.startsWith('//') ? returnPath : '/admin'
    markPostImpersonationExit()
    window.location.assign(dest)
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Wrench className="h-4 w-4 text-amber-600" />
            <DialogTitle>Dev toolkit</DialogTitle>
            {session.impersonating && (
              <Badge variant="outline" className="border-amber-500 text-amber-700">
                Impersonating
              </Badge>
            )}
            <span className="ml-auto rounded border bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
              ~
            </span>
          </div>
        </DialogHeader>

        <div className="grid gap-x-6 gap-y-2 md:grid-cols-2">
          <div>
            {!hasLeftContext && (
              <div className="px-3 py-6 text-center">
                <p className="text-xs font-medium text-muted-foreground">No page context</p>
                <p className="mt-1 text-xs text-muted-foreground/80">
                  Visit a review session, organization, or admin detail page (user, org, or session) to see
                  context-specific actions here.
                </p>
              </div>
            )}

            {session.impersonating && (
              <div className="space-y-0.5">
                <SectionHeader>Impersonation</SectionHeader>
                <div className="px-3 pb-1 text-xs text-muted-foreground">
                  Viewing as <span className="font-medium text-foreground">{signedInEmail}</span>
                  {session.realUser?.email && (
                    <>
                      {' '}
                      as <span className="font-medium text-foreground">{session.realUser.email}</span>
                    </>
                  )}
                </div>
                <ToolkitRow
                  icon={Eye}
                  label={exiting ? 'Exiting…' : 'Exit impersonation'}
                  onClick={() => void exitImpersonation()}
                  disabled={exiting}
                />
              </div>
            )}

            {sessionId && (
              <div className="space-y-0.5">
                <SectionHeader>This session</SectionHeader>
                <div className="px-3 pb-1 font-mono text-[11px] text-muted-foreground">{sessionId}</div>
                <ToolkitRow
                  icon={Video}
                  label="PostHog: sessions hitting this URL"
                  href={getPostHogReplayUrl({ type: 'currentUrl', substring: sessionId }).url}
                />
                <ToolkitRow
                  icon={Activity}
                  label="Open in Temporal"
                  href={getTemporalWorkflowUrl(`review-${sessionId}`)}
                />
                <ToolkitRow
                  icon={Database}
                  label="Supabase: review_sessions row"
                  href={getSupabaseStudioUrl({
                    table: 'review_sessions',
                    filter: { column: 'id', value: sessionId },
                    tableIds,
                  })}
                  disabledReason="Supabase URL not available"
                />
                <ToolkitRow
                  icon={Database}
                  label="Supabase: review_papers"
                  href={getSupabaseStudioUrl({
                    table: 'review_papers',
                    filter: { column: 'session_id', value: sessionId },
                    tableIds,
                  })}
                  disabledReason="Supabase URL not available"
                />
                <ToolkitRow
                  icon={Database}
                  label="Supabase: review_comments"
                  href={getSupabaseStudioUrl({
                    table: 'review_comments',
                    filter: { column: 'session_id', value: sessionId },
                    tableIds,
                  })}
                  disabledReason="Supabase URL not available"
                />
                <ToolkitRow icon={Copy} label="Copy session id" onClick={() => copy(sessionId, 'Session id')} />
              </div>
            )}

            {orgCode && (
              <div className="space-y-0.5">
                <SectionHeader>This org</SectionHeader>
                <div className="px-3 pb-1 font-mono text-[11px] text-muted-foreground">{orgCode}</div>
                <ToolkitRow
                  icon={Video}
                  label="PostHog: sessions hitting org URL"
                  href={
                    getPostHogReplayUrl({
                      type: 'currentUrl',
                      substring: `/organizations/${orgCode}`,
                    }).url
                  }
                />
                <ToolkitRow
                  icon={Video}
                  label={
                    orgMemberIds === null
                      ? 'PostHog: sessions by org members (loading…)'
                      : orgMemberIds.length === 0
                        ? 'PostHog: sessions by org members (none)'
                        : `PostHog: sessions by org members (${orgMemberIds.length})`
                  }
                  href={
                    orgMemberIds && orgMemberIds.length > 0
                      ? getPostHogReplayUrl({ type: 'users', userIds: orgMemberIds }).url
                      : null
                  }
                  disabledReason={orgMemberIds === null ? 'Loading members…' : 'No members on this org'}
                />
                <ToolkitRow
                  icon={Database}
                  label="Supabase: organizations row"
                  href={getSupabaseStudioUrl({
                    table: 'organizations',
                    filter: { column: 'code', value: orgCode.toUpperCase() },
                    tableIds,
                  })}
                  disabledReason="Supabase URL not available"
                />
                <ToolkitRow icon={Copy} label="Copy org code" onClick={() => copy(orgCode, 'Org code')} />
              </div>
            )}

            {pageUserId && pagePosthog && (
              <div className="space-y-0.5">
                <SectionHeader>This user (from page)</SectionHeader>
                <div className="px-3 pb-1 font-mono text-[11px] text-muted-foreground">{pageUserId}</div>
                <ToolkitRow icon={Video} label="PostHog: session replays" href={pagePosthog.url} />
                <ToolkitRow
                  icon={Activity}
                  label="Temporal: user queue"
                  href={getTemporalWorkflowUrl(`user-queue-${pageUserId}`)}
                />
                <ToolkitRow
                  icon={Database}
                  label="Supabase: users row"
                  href={getSupabaseStudioUrl({
                    table: 'users',
                    filter: { column: 'id', value: pageUserId },
                    tableIds,
                  })}
                  disabledReason="Supabase URL not available"
                />
                <ToolkitRow icon={Copy} label="Copy user id" onClick={() => copy(pageUserId, 'User id')} />
              </div>
            )}
          </div>

          <div>
            <div className="space-y-0.5">
              <SectionHeader>Signed in</SectionHeader>
              <div className="px-3 pb-1 text-xs text-muted-foreground">{signedInEmail}</div>
              <ToolkitRow icon={Video} label="PostHog: session replays" href={signedInPosthog.url} />
              <ToolkitRow
                icon={Activity}
                label="Temporal: user queue"
                href={getTemporalWorkflowUrl(`user-queue-${signedInId}`)}
              />
              <ToolkitRow
                icon={Database}
                label="Supabase: users row"
                href={getSupabaseStudioUrl({
                  table: 'users',
                  filter: { column: 'id', value: signedInId },
                  tableIds,
                })}
                disabledReason="Supabase URL not available"
              />
              <ToolkitRow icon={Copy} label="Copy user id" onClick={() => copy(signedInId, 'User id')} />
            </div>

            <div className="space-y-0.5">
              <SectionHeader>Admin</SectionHeader>
              <ToolkitRow icon={Users} label="Users" href="/admin/users" external={false} />
              <ToolkitRow icon={Building2} label="Organizations" href="/admin/organizations" external={false} />
              <ToolkitRow icon={Activity} label="Sessions" href="/admin/sessions" external={false} />
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
